import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { count } from '../../i18n/format';
import { percent } from './buckets';
import { OUTCOME_COLOURS } from './outcomes';

export interface OutcomeRow {
  readonly key: string;
  readonly label: string;
  readonly total: number;
  readonly completed: number;
  readonly open: number;
  readonly noShow: number;
  readonly cancelled: number;
}

/** Past this many rows the chart becomes a list; the rest are one click away, never dropped. */
const FIRST_ROWS = 8;

/**
 * A ranking where each bar is split by how the appointments went (CR-33).
 *
 * "Which service is busiest" and "which service loses the most patients" are two questions, and a
 * plain bar answers only the first. Each bar here is its row's total, scaled to the largest row,
 * and divided into completed, still open and no-show-or-cancelled — orchid, the light orchid step
 * and apricot, the two hues tokens.css validated for colour-vision deficiency plus a lightness
 * step of the first. A doctor whose bar is a third apricot is visible before any number is read,
 * and the figure beside it says the same in words, so the colour never carries it alone.
 *
 * No-show and cancelled share a segment: a third hue would fail the palette's separation rule, and
 * the status chart beside this one tells them apart.
 */
export function OutcomeBars({
  rows,
  selected,
  onSelect,
  selectLabel,
}: {
  readonly rows: readonly OutcomeRow[];
  readonly selected?: string;
  readonly onSelect?: (key: string) => void;
  readonly selectLabel?: (row: OutcomeRow) => string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const largest = rows.reduce((running, row) => Math.max(running, row.total), 0);
  const shown = expanded ? rows : rows.slice(0, FIRST_ROWS);

  return (
    <>
      <ol className="bars bars--plain">
        {shown.map((row) => {
          const lost = row.noShow + row.cancelled;
          const breakdown = t('overview.breakdown', {
            completed: row.completed,
            open: row.open,
            noShow: row.noShow,
            cancelled: row.cancelled,
          });
          const content = (
            <>
              <span className="bars__label" title={row.label}>
                {row.label}
              </span>
              <span className="bars__track">
                <span
                  className="bars__stack"
                  style={{ inlineSize: `${largest > 0 ? (row.total / largest) * 100 : 0}%` }}
                >
                  {row.completed > 0 ? (
                    <span style={{ flexGrow: row.completed, background: OUTCOME_COLOURS.completed }} />
                  ) : null}
                  {row.open > 0 ? <span style={{ flexGrow: row.open, background: OUTCOME_COLOURS.open }} /> : null}
                  {lost > 0 ? <span style={{ flexGrow: lost, background: OUTCOME_COLOURS.lost }} /> : null}
                </span>
              </span>
              <span className="bars__value numeric">
                {count(row.total)}
                {lost > 0 ? (
                  <span className="bars__note">{t('overview.lostShare', { share: percent(lost, row.total) })}</span>
                ) : null}
              </span>
              <span className="chart-tip" role="presentation">
                <strong>{row.label}</strong>
                <span>{breakdown}</span>
              </span>
            </>
          );
          return (
            <li key={row.key}>
              {onSelect ? (
                <button
                  type="button"
                  className="bars__row bars__row--button has-tip"
                  aria-pressed={selected === row.key}
                  aria-label={`${selectLabel ? selectLabel(row) : row.label}. ${breakdown}`}
                  onClick={() => onSelect(row.key)}
                >
                  {content}
                </button>
              ) : (
                <div className="bars__row has-tip" tabIndex={0} aria-label={`${row.label}. ${breakdown}`}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {rows.length > FIRST_ROWS ? (
        <button
          type="button"
          className="button button--ghost button--compact"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t('overview.showFewer') : t('overview.showAll', { count: rows.length })}
        </button>
      ) : null}
    </>
  );
}

/** The key for the three outcome colours, worded once for every chart that uses them. */
export function OutcomeLegend() {
  const { t } = useTranslation();
  return (
    <div className="chart__legend">
      <span>
        <span className="chart__key" style={{ background: OUTCOME_COLOURS.completed }} />
        {t('overview.completed')}
      </span>
      <span>
        <span className="chart__key" style={{ background: OUTCOME_COLOURS.open }} />
        {t('overview.open')}
      </span>
      <span>
        <span className="chart__key" style={{ background: OUTCOME_COLOURS.lost }} />
        {t('overview.lost')}
      </span>
    </div>
  );
}
